// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface LibraryItem {
  _id: string;
  title: string;
  description?: string;
  sourceDomain?: string;
  sourceType: string;
  contentType: string;
  originalUrl: string;
  canonicalUrl?: string;
  thumbnailUrl?: string;
  author?: string;
  publisher?: string;
  publishedAt?: string;
  createdAt?: string;
  extractedText?: string;
  mediaUrl?: string;
  readState: 'unread' | 'reading' | 'read';
  savedState: 'saved' | 'archived' | 'deleted';
  isFavorite?: boolean;
  tags?: string[];
  collectionIds?: string[];
  note?: string;
}

interface CollectionItem {
  _id: string;
  name: string;
}

interface ReaderDetailPaneProps {
  item: LibraryItem | null;
  collections: CollectionItem[];
  onBack?: () => void;
  onToggleRead: (item: LibraryItem) => void;
  onToggleFavorite: (item: LibraryItem) => void;
  onToggleArchive: (item: LibraryItem) => void;
  onDelete: (item: LibraryItem) => void;
  onUpdateNote: (item: LibraryItem, note: string) => void;
  onUpdateTags: (item: LibraryItem, tags: string[]) => void;
  onUpdateCollections: (item: LibraryItem, collectionIds: string[]) => void;
  showBackButton?: boolean;
}

function calculateReadingTime(text?: string) {
  if (!text) return '1 min read';
  const clean = text.replace(/<[^>]*>/g, ' ');
  const words = clean.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

export default function ReaderDetailPane({
  item,
  collections = [],
  onBack,
  onToggleRead,
  onToggleFavorite,
  onToggleArchive,
  onDelete,
  onUpdateNote,
  onUpdateTags,
  onUpdateCollections,
  showBackButton = false,
}: ReaderDetailPaneProps) {
  const [fontSerif, setFontSerif] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [newTagInput, setNewTagInput] = useState('');
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (item) {
      setNoteText(item.note || '');
    }
  }, [item?._id]);

  if (!item) {
    return (
      <main className="reader-detail-pane" aria-label="Reading Pane">
        <div className="reader-empty-state">
          <i className="fi fi-rr-book-open-cover reader-empty-icon" />
          <div className="reader-empty-title">Select an item to read</div>
          <div className="reader-empty-subtitle">
            Choose an article, podcast, or feed item from the list to start reading in distraction-free mode.
          </div>
        </div>
      </main>
    );
  }

  const isRead = item.readState === 'read';
  const isFavorite = !!item.isFavorite;
  const isArchived = item.savedState === 'archived';

  const handleSaveNote = () => {
    setIsNoteSaving(true);
    onUpdateNote(item, noteText);
    setTimeout(() => setIsNoteSaving(false), 500);
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTagInput.trim()) {
      e.preventDefault();
      const currentTags = item.tags || [];
      const newTag = newTagInput.trim().toLowerCase();
      if (!currentTags.includes(newTag)) {
        onUpdateTags(item, [...currentTags, newTag]);
      }
      setNewTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = item.tags || [];
    onUpdateTags(item, currentTags.filter((t) => t !== tagToRemove));
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(item.originalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <main className="reader-detail-pane" aria-label="Reading Pane">
      {/* Header Actions Toolbar */}
      <div className="reader-detail-header-actions">
        <div className="reader-detail-actions-group">
          {showBackButton && (
            <button
              className="reader-btn reader-btn-icon"
              onClick={onBack}
              title="Back to list"
              aria-label="Back to content list"
            >
              <i className="fi fi-rr-arrow-left" />
            </button>
          )}

          {/* Mark read / unread */}
          <button
            className={`reader-btn-icon ${isRead ? 'active' : ''}`}
            onClick={() => onToggleRead(item)}
            title={isRead ? 'Mark as unread' : 'Mark as read'}
          >
            <i className={`fi fi-rr-${isRead ? 'check' : 'circle'}`} />
          </button>

          {/* Star / Favorite */}
          <button
            className={`reader-btn-icon ${isFavorite ? 'active' : ''}`}
            onClick={() => onToggleFavorite(item)}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <i className={`fi fi-${isFavorite ? 'sr-star' : 'rr-star'}`} style={{ color: isFavorite ? '#eab308' : 'inherit' }} />
          </button>

          {/* Archive */}
          <button
            className={`reader-btn-icon ${isArchived ? 'active' : ''}`}
            onClick={() => onToggleArchive(item)}
            title={isArchived ? 'Unarchive' : 'Archive'}
          >
            <i className="fi fi-rr-box-alt" />
          </button>

          {/* Collection Select */}
          <select
            value={(item.collectionIds && item.collectionIds[0]) || ''}
            onChange={(e) => {
              const val = e.target.value;
              onUpdateCollections(item, val ? [val] : []);
            }}
            className="reader-btn"
            style={{ fontSize: '0.78rem', height: '34px', cursor: 'pointer' }}
            title="Move to collection"
          >
            <option value="">Move to Collection...</option>
            {collections.map((col) => (
              <option key={col._id} value={col._id}>
                {col.name}
              </option>
            ))}
          </select>
        </div>

        <div className="reader-detail-actions-group">
          {/* Font Toggle (Sans vs Serif) */}
          <button
            className={`reader-btn ${fontSerif ? 'reader-btn-primary' : ''}`}
            style={{ fontSize: '0.78rem', padding: '4px 8px' }}
            onClick={() => setFontSerif(!fontSerif)}
            title="Toggle serif typography"
          >
            <span style={{ fontFamily: 'Georgia, serif', fontWeight: 600 }}>T</span>
            <span>{fontSerif ? 'Serif' : 'Sans'}</span>
          </button>

          {/* Copy link */}
          <button
            className="reader-btn-icon"
            onClick={handleCopyLink}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            <i className={`fi fi-rr-${copied ? 'check' : 'copy'}`} />
          </button>

          {/* Open Original */}
          <a
            href={item.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="reader-btn"
            style={{ fontSize: '0.8rem' }}
            title="Open original website"
          >
            <span>Original</span>
            <i className="fi fi-rr-arrow-up-right" style={{ fontSize: '0.75rem' }} />
          </a>

          {/* Delete item */}
          <button
            className="reader-btn-icon"
            onClick={() => onDelete(item)}
            title="Delete from library"
            style={{ color: '#ef4444' }}
          >
            <i className="fi fi-rr-trash" />
          </button>
        </div>
      </div>

      {/* Scrollable Reader Document */}
      <div className="reader-detail-scroll">
        <article className="reader-article-container">
          <header className="reader-article-header">
            <div className="reader-article-meta-line">
              <span style={{ fontWeight: 600 }}>{item.sourceDomain || 'Web'}</span>
              <span>•</span>
              <span style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                {item.sourceType || 'article'}
              </span>
              <span>•</span>
              <span>{calculateReadingTime(item.extractedText || item.description)}</span>
              {item.publishedAt && (
                <>
                  <span>•</span>
                  <span>{new Date(item.publishedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                </>
              )}
            </div>

            <h1 className="reader-article-title">{item.title}</h1>

            <div className="reader-article-byline">
              {item.author && <span>By <strong>{item.author}</strong></span>}
              {item.publisher && item.publisher !== item.author && (
                <span>Published in <em>{item.publisher}</em></span>
              )}
              <a href={item.originalUrl} target="_blank" rel="noopener noreferrer">
                Visit original source →
              </a>
            </div>
          </header>

          {/* Video / Embed Player */}
          {item.mediaUrl && (item.sourceType === 'video' || item.contentType === 'video') && (
            <div className="reader-media-embed">
              <iframe
                src={item.mediaUrl}
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', aspectRatio: '16/9', border: 0 }}
              />
            </div>
          )}

          {/* Audio / Podcast Player */}
          {item.mediaUrl && (item.sourceType === 'podcast' || item.contentType === 'audio') && (
            <div className="reader-audio-player-wrap">
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--reader-text)' }}>
                <i className="fi fi-rr-headphones" style={{ marginRight: '6px' }} />
                Listen to Podcast / Audio Enclosure
              </div>
              <audio controls src={item.mediaUrl} preload="metadata">
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {/* Article Body Content */}
          <div
            className={`reader-article-body ${fontSerif ? 'serif' : ''}`}
            dangerouslySetInnerHTML={{
              __html:
                item.extractedText && item.extractedText.length > 50
                  ? item.extractedText
                  : item.description
                  ? `<p>${item.description}</p>`
                  : '<p style="color:var(--reader-text-muted);font-style:italic;">No extracted body text available for this link. You can read it directly at the original source above.</p>',
            }}
          />

          {/* Tags Manager Section */}
          <div style={{ marginTop: '36px', paddingTop: '20px', borderTop: '1px solid var(--reader-border-subtle)' }}>
            <div className="reader-notes-title">
              <i className="fi fi-rr-label" />
              <span>Tags</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
              {item.tags &&
                item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="reader-item-tag"
                    style={{ fontSize: '0.8rem', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    #{tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                      title="Remove tag"
                    >
                      ×
                    </button>
                  </span>
                ))}
              <input
                type="text"
                placeholder="+ Add tag (Press Enter)"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                style={{
                  height: '28px',
                  padding: '0 8px',
                  fontSize: '0.78rem',
                  border: '1px dashed var(--reader-border)',
                  borderRadius: '3px',
                  background: 'transparent',
                  color: 'var(--reader-text)',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Personal Notes Editor Box */}
          <div className="reader-notes-box">
            <div className="reader-notes-title">
              <i className="fi fi-rr-edit" />
              <span>Personal Notes & Highlights</span>
              {isNoteSaving && (
                <span style={{ fontSize: '0.72rem', color: 'var(--reader-accent)', marginLeft: 'auto' }}>
                  Saving…
                </span>
              )}
            </div>
            <textarea
              className="reader-notes-textarea"
              placeholder="Jot down notes, summary, key takeaways, or follow-ups for this article..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={handleSaveNote}
            />
          </div>
        </article>
      </div>
    </main>
  );
}
